/**
 * Wizard card for the smoke_test step (the Verify-group closer).
 *
 * Clicking "Run smoke test" kicks off the onboard-smoke-test edge function,
 * which fires Playwright booking scenarios against the client's live site
 * (https://book.{mainDomain}) via the limo-anywhere service and reports back
 * asynchronously. The card shows an in_progress spinner while the run is in
 * flight, then a per-scenario pass/fail/skip table once the callback lands.
 * Manual Refresh re-runs (no cron), mirroring VerifyAdsActiveCard.
 *
 * Purchase scenarios charge the platform TEST Stripe account (never the
 * client's live Stripe) and are flagged is_test so no real reservation reaches
 * the client's dispatch/CRM.
 */
import { CheckCircle2, Loader2, MinusCircle, RefreshCw, Sparkles, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { OnboardingTask } from '@limo/database'
import { useInvokeOnboardingStep } from '@/hooks/useOnboarding'
import { useToast } from '@/hooks/use-toast'
import { StepCard } from './StepCard'

interface SmokeTestCardProps {
  task: OnboardingTask
  accountId: string
  runId: string
}

interface ScenarioResult {
  scenario: string
  status: 'passed' | 'failed' | 'skipped'
  dataLayerEvents?: string[]
  error?: string
  durationMs?: number
}

interface SmokeTestResult {
  baseUrl?: string
  scenarios?: string[]
  results?: ScenarioResult[]
  passed?: boolean
  startedAt?: string
  checkedAt?: string
}

const SCENARIO_LABELS: Record<string, string> = {
  point_to_point: 'Point-to-point booking',
  round_trip: 'Round-trip booking',
  hourly: 'Hourly / charter booking',
  contact_form: 'Contact form (lead)',
  quote_only: 'Quote-only vehicle',
}

function StatusIcon({ status }: { status: ScenarioResult['status'] }) {
  if (status === 'passed') return <CheckCircle2 className="h-4 w-4 text-green-600" />
  if (status === 'skipped') return <MinusCircle className="h-4 w-4 text-muted-foreground" />
  return <XCircle className="h-4 w-4 text-red-600" />
}

export function SmokeTestCard({ task, accountId, runId }: SmokeTestCardProps) {
  const { toast } = useToast()
  const invokeStep = useInvokeOnboardingStep(runId)

  const result = (task.result ?? {}) as SmokeTestResult
  const results = result.results ?? []
  const isRunning = task.status === 'in_progress' || invokeStep.isPending
  const hasResults = results.length > 0

  const runSmokeTest = () =>
    invokeStep.mutate(
      { functionName: 'onboard-smoke-test', accountId, params: { action: 'start' } },
      {
        onError: (error) =>
          toast({
            title: 'Could not start smoke test',
            description: error.message,
            variant: 'destructive',
          }),
      }
    )

  return (
    <StepCard
      task={task}
      manualFallback={
        <div className="space-y-2">
          <p>
            Runs Playwright booking scenarios against the live site
            {result.baseUrl ? (
              <>
                {' '}(<code>{result.baseUrl}</code>)
              </>
            ) : (
              <> (<code>https://book.&#123;domain&#125;</code>)</>
            )}{' '}
            and asserts the dataLayer events fire. Purchase scenarios use the
            platform <strong>test</strong> Stripe account, so nothing is charged
            and no reservation reaches the client&rsquo;s dispatch/CRM.
          </p>
          <p>
            Prerequisites: the domain is live (DNS verified), Stripe Connect is
            done, and the GTM/Stape tagging is in place. Manual path: open the
            site and run a booking with Stripe test card{' '}
            <code>4242 4242 4242 4242</code>, watching <code>window.dataLayer</code>.
          </p>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        {result.baseUrl && (
          <p className="text-xs text-muted-foreground">
            Target:{' '}
            <a
              href={result.baseUrl}
              target="_blank"
              rel="noreferrer"
              className="font-mono underline"
            >
              {result.baseUrl}
            </a>
          </p>
        )}

        {isRunning && !hasResults ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Running booking scenarios on the live site… this can take a minute or two.</span>
          </div>
        ) : hasResults ? (
          <>
            <div className="overflow-hidden rounded-md border">
              <table className="w-full text-xs">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-2 py-1 text-left font-medium">Scenario</th>
                    <th className="px-2 py-1 text-left font-medium">Result</th>
                    <th className="px-2 py-1 text-left font-medium">Detail</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {results.map((r) => (
                    <tr key={r.scenario}>
                      <td className="px-2 py-1">{SCENARIO_LABELS[r.scenario] ?? r.scenario}</td>
                      <td className="px-2 py-1">
                        <span className="flex items-center gap-1.5 capitalize">
                          <StatusIcon status={r.status} />
                          {r.status}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-muted-foreground">
                        {r.status === 'failed'
                          ? r.error ?? 'Failed'
                          : r.status === 'skipped'
                            ? r.error ?? 'Not applicable'
                            : (r.dataLayerEvents ?? []).join(', ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant={task.status === 'done' ? 'outline' : 'default'}
                onClick={runSmokeTest}
                disabled={isRunning}
              >
                {isRunning ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Re-run
              </Button>
              {task.status === 'done' && (
                <span className="flex items-center gap-1 text-xs text-green-700">
                  <CheckCircle2 className="h-4 w-4" /> All scenarios passed
                </span>
              )}
            </div>
          </>
        ) : (
          <div className="space-y-2">
            <p className="text-muted-foreground">
              Runs point-to-point, hourly, contact-form, and quote-only scenarios
              against the live site and confirms the booking funnel + tracking work.
            </p>
            <Button type="button" size="sm" onClick={runSmokeTest} disabled={isRunning}>
              {isRunning ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              Run smoke test
            </Button>
          </div>
        )}
      </div>
    </StepCard>
  )
}
